import { Inject, Injectable } from '@angular/core';
import { BehaviorSubject, combineLatest, of, timer } from 'rxjs';
import { exhaustMap, map, switchMap } from 'rxjs/operators';
import { Destination } from '../destinations/destination.entity';
import { DestinationState } from '../destinations/destination.state';
import { ProductsService } from '../products/products.service';
import { ProductGroupsService } from '../products/product-groups.service';
import { MainTableModifiersService } from '../monitor/main-table-modifiers.service';
import { MONITOR_GATEWAY, MonitorGateway } from '../monitor/monitor.gateway';

const ONCE_5_SEC = 5 * 1000;

@Injectable({
  providedIn: 'root',
})
export class InstrumentByShipmentDestinationService {
  destination$ = new BehaviorSubject<Destination | null>(null);

  constructor(
    @Inject(MONITOR_GATEWAY) private readonly gateway: MonitorGateway,
    private readonly destinationState: DestinationState,
    private readonly productsService: ProductsService,
    private readonly productGroupsService: ProductGroupsService,
    private readonly mainTableModifiersService: MainTableModifiersService,
  ) {
    destinationState.currentDestination$.subscribe(this.destination$);
  }

  setDestination(destination: Destination) {
    this.destination$.next(destination);
  }

  getInstrument(shipmentCode: string) {
    return combineLatest([
      this.destination$,
      of([shipmentCode]),
      this.productsService.selectedProductsOfCurrentGroup$,
      this.productGroupsService.currentProductGroup$.pipe(
        map((product) => product.code),
      ),
      this.mainTableModifiersService.modifier$,
    ]).pipe(
      switchMap(
        ([destination, shipments, activeProducts, productCode, modifier]) =>
          timer(0, ONCE_5_SEC).pipe(
            exhaustMap(() => {
              return this.gateway
                .getInstruments(
                  productCode,
                  destination?.id ?? null,
                  shipments,
                  activeProducts,
                  modifier,
                )
                .pipe(map((instruments) => instruments[0]));
            }),
          ),
      ),
    );
  }
}
